import { parseDbTimestamp } from "@repo/shared";
import type { ClientOrdersResponse, PortfolioOrderStatus, Translated } from "@repo/shared/types";
import { Copy } from "lucide-react";
import { OrderTypeBadge } from "@/components/orders/order-type-badge";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { cn } from "@/lib/utils";
import { T } from "./order-detail-dialog.i18n";
import { formatUnits, formatWholeAmount, getOrderProductName } from "./order-product-name";

/** One order row from the grouped client-orders payload. */
export type ClientOrderRow =
  ClientOrdersResponse["flowInstances"][number]["goals"][number]["portfolios"][number]["orders"][number];

type OrderDetailT = Translated<typeof T>;

const STATUS_VARIANTS: Record<
  PortfolioOrderStatus,
  "secondary" | "info" | "success" | "warning" | "destructive"
> = {
  pending: "secondary",
  submitted: "info",
  executed: "success",
  partially_executed: "warning",
  failed: "destructive",
  cancelled: "secondary",
  modified: "warning",
};

function formatTimestamp(value: string | null, formatDateTime: (d: Date) => string): string | null {
  if (!value) return null;
  const d = parseDbTimestamp(value);
  if (!d) return value;
  const formatted = formatDateTime(d);
  return formatted === "" ? value : formatted;
}

/** Order target/executed rendered per the order's mode, the other figure in
 * parens when known: units orders lead with units (the instruction), amount
 * orders with the amount. A units order's target amount is units × price at
 * advice time, so it is marked as an estimate; executed figures are the bank's
 * and never are. An amount order's target has no units yet. */
function formatOrderQuantity(
  order: ClientOrderRow,
  which: "target" | "executed",
  t: OrderDetailT,
  lang: string,
  formatAmount: (v: number) => string,
): string | null {
  const units = which === "target" ? order.targetUnits : order.executedUnits;
  const amount = which === "target" ? order.targetAmount : order.executedAmount;
  if (order.orderAmountType === "units") {
    if (units == null) return null;
    if (amount == null) return formatUnits(units, lang);
    const estimate = which === "target" ? `${t.estimatedShort} ` : "";
    return `${formatUnits(units, lang)} (${estimate}${formatAmount(amount)})`;
  }
  if (amount == null) return null;
  return units != null
    ? `${formatAmount(amount)} (${formatUnits(units, lang)})`
    : formatAmount(amount);
}

// Single label/value pair. Ids render mono with a copy affordance; absent
// values render as an em dash.
function DetailField({
  label,
  value,
  mono,
  copyable,
  copyLabel,
  wrap,
  className,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  copyable?: boolean;
  copyLabel?: string;
  /** Let long free text wrap instead of truncating. */
  wrap?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <div className="text-xs text-muted-foreground">{label}</div>
      {value ? (
        <div className="flex items-center gap-1 min-w-0">
          <span
            className={cn(
              "text-sm",
              wrap ? "whitespace-pre-wrap break-words" : "truncate",
              mono && "font-mono text-xs",
            )}
            title={value}
          >
            {value}
          </span>
          {copyable && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="p-0.5 rounded hover:bg-muted shrink-0"
                  aria-label={copyLabel}
                  onClick={() => void navigator.clipboard.writeText(value)}
                >
                  <Copy className="h-3 w-3 text-muted-foreground" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{copyLabel}</TooltipContent>
            </Tooltip>
          )}
        </div>
      ) : (
        <span className="text-sm text-muted-foreground">—</span>
      )}
    </div>
  );
}

function SectionHeading({ children }: { children: string }) {
  return (
    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground border-b pb-1">
      {children}
    </div>
  );
}

// Three fields per row; two on narrow screens.
const GRID = "grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2";

type OrderDetailDialogProps = {
  /** The order to show; null = dialog closed. */
  order: ClientOrderRow | null;
  onClose: () => void;
};

export function OrderDetailDialog({ order, onClose }: OrderDetailDialogProps) {
  const { userLanguage } = useUserLanguage();
  const { formatDateTime, formatNumber } = useRegionalFormat();
  // QL-547 — amounts are whole numbers, like the Accounts tab; prices and
  // weights keep their decimals.
  const formatAmount = (value: number) => formatWholeAmount(value, formatNumber);
  const t = useT(T);

  const isSwitch = order?.orderType === "switch";
  const isUnits = order?.orderAmountType === "units";

  // Fields that appear in both layouts (a switch's sell side, or the plain
  // Instrument / Order sections).
  const typeField = order && (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{t.type}</div>
      <OrderTypeBadge orderType={order.orderType} language={userLanguage} />
    </div>
  );
  const modeField = <DetailField label={t.mode} value={isUnits ? t.modeUnits : t.modeAmount} />;
  const executedField = order && (
    <DetailField
      label={isUnits ? t.executedUnits : t.executedAmount}
      value={formatOrderQuantity(order, "executed", t, userLanguage, formatAmount)}
    />
  );
  const priceField = order && (
    <DetailField
      label={t.price}
      value={order.executedPrice != null ? formatNumber(order.executedPrice) : null}
    />
  );
  const currencyField = order && <DetailField label={t.currency} value={order.currency} />;
  const wrapperTypesField = order && (
    <div className="col-span-full min-w-0">
      <div className="text-xs text-muted-foreground">{t.supportedWrapperTypes}</div>
      {(order.supportedWrapperTypes?.length ?? 0) > 0 ? (
        <div className="flex flex-wrap gap-1 pt-0.5">
          {order.supportedWrapperTypes.map((w) => (
            <Badge key={w} variant={w === order.accountWrapperType ? "secondary" : "outline"}>
              {w}
            </Badge>
          ))}
        </div>
      ) : (
        <span className="text-sm text-muted-foreground">—</span>
      )}
    </div>
  );

  return (
    <Dialog open={order !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        {order && (
          <>
            <DialogHeader>
              {/* QL-544 — a switch is titled by the fund being bought, like its
                  row in the orders table. */}
              <DialogTitle>
                {isSwitch
                  ? getOrderProductName(order.targetProductName, userLanguage, order.targetIsin)
                  : getOrderProductName(order.productName, userLanguage, order.isin)}
              </DialogTitle>
              <DialogDescription>
                {isSwitch
                  ? t.switchFrom(getOrderProductName(order.productName, userLanguage, order.isin))
                  : t.dialogDescription}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {isSwitch ? (
                // QL-544 — a switch has two funds: the row's own fields are the
                // sell side, `target*` the buy side. Each side carries its own
                // instrument, buy side first like the title and the table row.
                <>
                  <div className="space-y-2">
                    <SectionHeading>{t.buySide}</SectionHeading>
                    <div className={GRID}>
                      <DetailField
                        label={t.instrument}
                        value={getOrderProductName(
                          order.targetProductName,
                          userLanguage,
                          order.targetIsin,
                        )}
                        className="sm:col-span-2"
                      />
                      <DetailField
                        label={t.isin}
                        value={order.targetIsin}
                        mono
                        copyable
                        copyLabel={t.copy}
                      />
                      <DetailField label={t.ticker} value={order.targetTicker} mono />
                      {typeField}
                      {/* Bank-reported; may fill progressively while the order
                          rests in partially_executed. */}
                      <DetailField
                        label={t.proceedsInvested}
                        value={
                          order.targetExecutedAmount != null
                            ? order.targetExecutedUnits != null
                              ? `${formatAmount(order.targetExecutedAmount)} (${formatUnits(order.targetExecutedUnits, userLanguage)})`
                              : formatAmount(order.targetExecutedAmount)
                            : null
                        }
                      />
                      <DetailField
                        label={t.price}
                        value={
                          order.targetExecutedPrice != null
                            ? formatNumber(order.targetExecutedPrice)
                            : null
                        }
                      />
                    </div>
                    {order.status === "partially_executed" && (
                      <p className="text-xs text-muted-foreground">{t.switchRestingNote}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <SectionHeading>{t.sellSide}</SectionHeading>
                    <div className={GRID}>
                      <DetailField
                        label={t.instrument}
                        value={getOrderProductName(order.productName, userLanguage, order.isin)}
                      />
                      <DetailField
                        label={t.isin}
                        value={order.isin}
                        mono
                        copyable
                        copyLabel={t.copy}
                      />
                      <DetailField label={t.ticker} value={order.ticker} mono />
                      {modeField}
                      {/* QL-283 — the authoritative instruction: percent of the
                          position (amount mode) or whole units (units mode); the
                          proceeds beside it are the advice-time estimate. */}
                      <DetailField
                        label={t.switchInstruction}
                        value={
                          order.switchPercent != null
                            ? t.switchPercentOf(String(order.switchPercent))
                            : order.targetUnits != null
                              ? t.switchUnitsOf(String(order.targetUnits))
                              : null
                        }
                      />
                      <DetailField
                        label={t.estimatedProceeds}
                        value={order.targetAmount != null ? formatAmount(order.targetAmount) : null}
                      />
                      {executedField}
                      {priceField}
                      {currencyField}
                      {wrapperTypesField}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <SectionHeading>{t.instrument}</SectionHeading>
                    <div className={GRID}>
                      <DetailField
                        label={t.isin}
                        value={order.isin}
                        mono
                        copyable
                        copyLabel={t.copy}
                      />
                      <DetailField label={t.ticker} value={order.ticker} mono />
                      {modeField}
                      {wrapperTypesField}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <SectionHeading>{t.order}</SectionHeading>
                    <div className={GRID}>
                      {typeField}
                      <DetailField
                        label={t.targetWeight}
                        value={
                          order.targetWeight != null ? `${order.targetWeight.toFixed(2)}%` : null
                        }
                      />
                      <DetailField
                        label={isUnits ? t.targetUnits : t.targetAmount}
                        value={formatOrderQuantity(order, "target", t, userLanguage, formatAmount)}
                      />
                      {executedField}
                      {priceField}
                      {currencyField}
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <SectionHeading>{t.accountRouting}</SectionHeading>
                <div className={GRID}>
                  <div className="min-w-0">
                    {/* The routed account is often a pending placeholder that is
                        invisible on the Accounts tab — show only the account TYPE
                        the instrument maps to, never its name/number. */}
                    <div className="text-xs text-muted-foreground">{t.wrapperType}</div>
                    {order.accountWrapperType ? (
                      <Badge variant="outline">{order.accountWrapperType}</Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </div>
                  <DetailField
                    label={t.accountExternalId}
                    value={order.accountExternalId}
                    mono
                    copyable
                    copyLabel={t.copy}
                  />
                  <DetailField
                    label={t.externalOrderId}
                    value={order.externalOrderId}
                    mono
                    copyable
                    copyLabel={t.copy}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <SectionHeading>{t.lifecycle}</SectionHeading>
                <div className={GRID}>
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">{t.status}</div>
                    <Badge variant={STATUS_VARIANTS[order.status]}>
                      {t.statusLabels[order.status]}
                    </Badge>
                  </div>
                  {/* Free text from the bank — the rest of the row, wrapping. */}
                  <DetailField
                    label={t.statusReason}
                    value={order.statusReason}
                    wrap
                    className="sm:col-span-2"
                  />
                  <DetailField
                    label={t.createdAt}
                    value={formatTimestamp(order.createdAt, formatDateTime)}
                  />
                  <DetailField
                    label={t.submittedAt}
                    value={formatTimestamp(order.submittedAt, formatDateTime)}
                  />
                  <DetailField
                    label={t.executedAt}
                    value={formatTimestamp(order.executedAt, formatDateTime)}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
